/* import __COLOCATED_TEMPLATE__ from './base.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable @intercom/intercom/no-default-task-ember-concurrency */
import Component from '@glimmer/component';
import { get, post, put } from 'embercom/lib/ajax';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import ajax from 'embercom/lib/ajax';
import { timeout } from 'ember-concurrency';

const BUCKET_LENGTH_LIMIT = 63;

export default class Base extends Component {
  @service appService;
  @service intl;
  @service notificationsService;
  @service intercomConfirmService;
  @service router;
  @service regionService;
  @tracked runJobsData = [];
  @tracked isEditMode = false;
  @tracked exportDefinition = null;
  @tracked exportDefinitionPersisted = false;
  @tracked lastExportEventJob = null;
  @tracked previousBucketName = '';
  @tracked bucketName = '';
  @tracked destinationType = 's3';
  @tracked azureStorageAccountName = '';
  @tracked dataPath = '';
  @tracked bulk = true;
  @tracked schedule = 'hourly';
  @tracked bucketVerified = false;
  @tracked showBucketVerificationBanner = false;
  currentPage = 1;
  perPage = 25;
  emailWaitTime = 30000;
  @tracked selectedTab = 'runs';
  @tracked canLoadMore = true;
  @tracked frequencies = [
    {
      text: this.intl.t(
        'settings.bulk-export.configure-data-export.schedule-card.step-two-frequency-hourly',
      ),
      value: 'hourly',
    },
    {
      text: this.intl.t(
        'settings.bulk-export.configure-data-export.schedule-card.step-two-frequency-daily',
      ),
      value: 'daily',
    },
  ];
  predefinedBucketPrefix = 'intercom-export-';
  maxBucketNameLength = BUCKET_LENGTH_LIMIT - this.predefinedBucketPrefix.length;

  @tracked savedDataFormat = 'JSON';
  @tracked selectedDataFormat = 'JSON';
  @tracked includeAttachments = false;
  @tracked savedIncludeAttachments = false;
  @tracked apiVersionOptions = [];
  @tracked lastStableApiVersion = undefined;
  @tracked selectedApiVersion = undefined;
  @tracked savedApiVersion = undefined;

  @tracked selectedSortingMethod = 'no_folders';
  @tracked skipEmptyFiles = false;

  constructor() {
    super(...arguments);
    if (this.appService.app.useBulkExportNewUI) {
      this.router.transitionTo('apps.app.settings.data.imports-exports.export-data');
      return;
    }
    this.getExportJobDefinition.perform();
    this.getApiVersions.perform();
  }

  @task
  *getApiVersions() {
    let response = yield get(`/ember/bulk_export/export_job_definitions/api_versions`, {
      app_id: this.appService.app.id,
    });
    this.apiVersionOptions = response?.map((apiVersion) => ({
      text: apiVersion.latest_public
        ? `${apiVersion.id} (${this.intl.t('settings.bulk-export.configure-data-export.data-objects-card.data-api-version.latest-public')})`
        : apiVersion.id,
      value: apiVersion.id,
    }));
    this.lastStableApiVersion = response?.find((apiVersion) => apiVersion.latest_public)?.id;
  }

  @task
  *getExportJobDefinition() {
    let response = yield get(`/ember/bulk_export/export_job_definitions`, {
      app_id: this.appService.app.id,
    });
    if (Array.isArray(response)) {
      this.exportDefinition = response[0];
    } else {
      this.exportDefinition = response;
    }

    if (!this.exportDefinition?.id) {
      this.notificationsService.notifyError(
        this.intl.t(
          'settings.bulk-export.configure-data-export.data-objects-card.actions.failed-to-load',
        ),
      );
      return;
    }

    this.exportDefinitionPersisted = true;
    this.loadJobDefinitionValues();

    if (this.exportDefinition.verified) {
      this.getRunJobs.perform(1);
    }
    this.setActiveTab();
  }

  @action
  loadJobDefinitionValues() {
    this.bulk = this.exportDefinition.bulk;
    this.schedule = this.exportDefinition.schedule;
    this.dataPath = this.exportDefinition.data_path;
    this.bucketName = this.exportDefinition.bucket_name;
    this.destinationType = this.exportDefinition.destination_type;
    this.azureStorageAccountName = this.exportDefinition.azure_storage_account_name || '';
    this.skipEmptyFiles = this.exportDefinition.skip_empty_files;
    this.selectedSortingMethod = this.calculateSelectedSortingMethod(this.exportDefinition);
    this.selectedDataFormat = this.exportDefinition.format;
    this.savedDataFormat = this.exportDefinition.format;
    this.includeAttachments = this.exportDefinition.include_attachments;
    this.savedIncludeAttachments = this.exportDefinition.include_attachments;
    this.selectedApiVersion = this.exportDefinition.data_api_version;
    this.savedApiVersion = this.exportDefinition.data_api_version;
  }

  @action
  setActiveTab() {
    if (this.exportDefinition.length === 0) {
      this.selectedTab = 'configuration';
    }
  }

  @action
  onLoadMore() {
    this.getRunJobs.perform();
  }

  @task
  *getRunJobs(page) {
    this.currentPage = page || this.currentPage;
    this.newRunJobsData = yield get(`/ember/bulk_export/export_job_runs`, {
      app_id: this.appService.app.id,
      export_job_definition_id: this.exportDefinition.id,
      page: this.currentPage,
      per_page: this.perPage,
    });
    this.runJobsData = [...this.runJobsData, ...this.newRunJobsData];
    this.lastExportEventJob = this.runJobsData[0];
    this.currentPage += 1;
    if (this.newRunJobsData.length < this.perPage) {
      this.canLoadMore = false;
    }
  }

  @action
  async deactivateExportDefinition() {
    let confirmOptions = {
      title: this.intl.t('settings.bulk-export.run-jobs.delete-warning-card.title'),
      primaryButtonType: 'primary-destructive',
      confirmButtonText: this.intl.t('settings.bulk-export.run-jobs.delete-action'),
      cancelButtonText: this.intl.t('settings.bulk-export.cancel-action'),
      body: this.intl.t('settings.bulk-export.run-jobs.delete-warning-card.description'),
    };
    let isConfirmed = await this.intercomConfirmService.confirm(confirmOptions);
    if (!isConfirmed) {
      return;
    }
    await ajax({
      url: `/ember/bulk_export/export_job_definitions/${this.exportDefinition.id}`,
      type: 'DELETE',
      data: JSON.stringify({
        app_id: this.appService.app.id,
      }),
      dataType: 'text',
    });
    this.exportDefinition = null;
    this.bucketName = '';
    this.destinationType = 's3';
    this.azureStorageAccountName = '';
    this.dataPath = '';
    this.selectedDataFormat = 'JSON';
    this.runJobsData = [];
    this.skipEmptyFiles = false;
    this.selectedSortingMethod = 'no_folders';
    this.schedule = 'hourly';
    this.exportDefinitionPersisted = false;
    this.selectedApiVersion = this.apiVersionOptions[0]?.value;
    this.savedApiVersion = this.apiVersionOptions[0]?.value;
    this.includeAttachments = false;
    this.savedIncludeAttachments = false;
    this.selectedDataFormat = 'JSON';
    this.savedDataFormat = 'JSON';

    this.isEditMode = false;
    this.selectedTab = 'runs';
  }

  @action
  async updateExportDefinitionDataObjects() {
    try {
      let response = await put(
        `/ember/bulk_export/export_job_definitions/${this.exportDefinition.id}/update_data_objects_settings`,
        {
          app_id: this.appService.app.id,
          data_format: this.selectedDataFormat,
          include_attachments: this.includeAttachments,
          data_api_version: this.selectedApiVersion,
        },
      );
      let { format, include_attachments, data_api_version } = response;
      this.savedDataFormat = format;
      this.savedIncludeAttachments = include_attachments;
      this.savedApiVersion = data_api_version;

      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.bulk-export.configure-data-export.data-objects-card.actions.success'),
      );
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('settings.bulk-export.configure-data-export.data-objects-card.actions.failed'),
      );
    }
  }

  @action
  resetBucketVerification() {
    this.bucketVerified = false;
    this.showBucketVerificationBanner = false;
    if (this.destinationType !== 'azure_blob') {
      this.azureStorageAccountName = '';
    }
  }

  @action
  resetSavedDataObjects() {
    this.selectedDataFormat = this.savedDataFormat;
    this.includeAttachments = this.savedIncludeAttachments;
    this.selectedApiVersion = this.savedApiVersion;
  }

  @action
  toggleEditMode(isEditing) {
    this.isEditMode = isEditing;
    this.schedule = 'hourly';
  }
  @action
  toggleBulk() {
    this.bulk = !this.bulk;
  }

  @task
  *resendVerificationEmail() {
    let response = yield get(
      `/ember/bulk_export/export_job_definitions/${this.exportDefinition.id}/resend_verification_email`,
      {
        app_id: this.appService.app.id,
      },
    );

    if (response.error) {
      this.notificationsService.notifyError(
        this.intl.t('settings.bulk-export.unverified-data-export.error'),
      );
    } else {
      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.bulk-export.unverified-data-export.confirmation'),
      );
    }

    yield timeout(this.emailWaitTime);
  }

  @action
  manualResendEmail() {
    return this.resendVerificationEmail.perform();
  }

  @action
  async createJobDefinition() {
    try {
      let confirmOptions = {
        title: this.intl.t('settings.bulk-export.start-modal.title'),
        primaryButtonType: 'primary',
        confirmButtonText: this.intl.t('settings.bulk-export.start-modal.confirm-action'),
        hideCancelButton: true,
        body: this.intl.t('settings.bulk-export.start-modal.description'),
      };
      let isConfirmed = await this.intercomConfirmService.confirm(confirmOptions);
      if (!isConfirmed) {
        return;
      }
      this.exportDefinition = await ajax({
        url: `/ember/bulk_export/export_job_definitions`,
        type: 'POST',
        data: JSON.stringify({
          app_id: this.appService.app.id,
          bulk: this.bulk,
          schedule: this.schedule,
          data_path: this.dataPath,
          bucket_name: this.bucketName,
          destination_type: this.destinationType,
          azure_storage_account_name:
            this.destinationType === 'azure_blob' ? this.azureStorageAccountName : undefined,
          data_format: this.selectedDataFormat,
          use_date_folders: this.useDateFolders(this.selectedSortingMethod),
          date_sorting_method: this.dateSortingMethod(this.selectedSortingMethod),
          skip_empty_files: this.skipEmptyFiles,
          include_attachments: this.includeAttachments,
          data_api_version: this.apiSelectedValueWithDefault,
        }),
      });
      this.loadJobDefinitionValues();
    } catch (error) {
      if (error?.errorThrown === 'Conflict') {
        this.notificationsService.notifyError(
          this.intl.t(
            'settings.bulk-export.configure-data-export.data-objects-card.actions.another-export-definition-already-exists',
          ),
        );
      } else {
        this.notificationsService.notifyError(
          this.intl.t('settings.bulk-export.run-jobs.fetch-error'),
        );
      }
    }
    this.exportDefinitionPersisted = true;
    this.selectedTab = 'runs';
    this.isEditMode = false;
  }

  @action
  async verifyBucket() {
    let requestData = {
      app_id: this.appService.app.id,
      bucket_name: this.bucketName,
      destination_type: this.destinationType,
    };
    if (this.destinationType === 'azure_blob') {
      requestData.azure_storage_account_name = this.azureStorageAccountName;
    }
    let response = await post(`/ember/bulk_export/bucket_verification`, requestData);
    if (!response.error) {
      this.bucketVerified = true;
      this.previousBucketName = this.bucketName;
    }
    this.showBucketVerificationBanner = true;
  }

  @action
  updateApiVersion(apiVersion) {
    this.selectedApiVersion = apiVersion;
  }

  @action
  toggleSkipEmptyFiles() {
    this.skipEmptyFiles = !this.skipEmptyFiles;
  }

  @action
  toggleIncludeAttachments() {
    this.includeAttachments = !this.includeAttachments;
  }

  @action
  updateAzureStorageAccountName(value) {
    this.azureStorageAccountName = value;
  }

  @action
  updateBucketName(value) {
    this.bucketName = value;
  }

  calculateSelectedSortingMethod(exportDefinition) {
    if (exportDefinition.use_date_folders) {
      if (exportDefinition.date_sorting_method === 'conversation_updated_date') {
        return 'conversation_updated_date_folders';
      } else {
        return 'export_date_folders';
      }
    } else {
      return 'no_folders';
    }
  }

  useDateFolders(selectedSortingMethod) {
    if (selectedSortingMethod === 'no_folders') {
      return false;
    } else {
      return true;
    }
  }

  dateSortingMethod(selectedSortingMethod) {
    if (selectedSortingMethod === 'export_date_folders') {
      return 'export_date';
    } else {
      return 'conversation_updated_date';
    }
  }

  get dataObjectsChanged() {
    return (
      this.savedDataFormat !== this.selectedDataFormat ||
      this.savedIncludeAttachments !== this.includeAttachments ||
      this.savedApiVersion !== this.selectedApiVersion
    );
  }

  get apiSelectedValueWithDefault() {
    if (this.selectedApiVersion) {
      return this.selectedApiVersion;
    }
    return this.apiVersionOptions[0]?.value;
  }

  get conversationsReferenceUrl() {
    if (this.apiSelectedValueWithDefault === this.lastStableApiVersion) {
      return 'https://developers.intercom.com/docs/references/rest-api/api.intercom.io/conversations/conversation';
    }
    if (this.apiSelectedValueWithDefault?.startsWith('1')) {
      return `https://developers.intercom.com/docs/references/${this.apiSelectedValueWithDefault}/rest-api/conversations/conversation-model`;
    }
    if (this.apiSelectedValueWithDefault) {
      return `https://developers.intercom.com/docs/references/${this.apiSelectedValueWithDefault.toLowerCase()}/rest-api/api.intercom.io/conversations/conversation`;
    }
    return null;
  }
}
